import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import { fetchHealthCareFacilityList } from '@/domains/asset-management/apis/healthcare-management';
import useAmbulanceManagement from '@/domains/asset-management/hooks/useAmbulanceManagement';
import type {
  AmbulanceDetailData,
  AmbulanceFormValues,
  ButtonState,
  CreateAmbulancePayload,
  UpdateAmbulancePayload,
} from '@/domains/asset-management/types/ambulance';
import type { HealthCareFacilityListResponse } from '@/domains/asset-management/types/healthcare';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

const AMBULANCE_SORT_FIELD_MAP: Record<string, string> = {
  ambulance_id: 'id',
  updated_time: 'updated_time',
};

const emptyFormValues: AmbulanceFormValues = {
  ambulance_id: null,
  police_number: null,
  ambulance_type: null,
  ambulance_status: null,
  ambulance_condition: null,
  vehicle_type: null,
  manufacturer_year: null,
  health_care_facility_id: null,
  pic_name: null,
  pic_phone_number: null,
  health_care_pic_phone: null,
};

const AmbulanceManagement = () => {
  const { t } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedAmbulance, setSelectedAmbulance] = useState<{ ambulance_id: number | null }>({
    ambulance_id: null,
  });
  const selectedAmbulanceRef = useRef(selectedAmbulance);
  const [typeFilter, setTypeFilter] = useState<string>('');

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedAmbulanceRef.current = selectedAmbulance;
  }, [hasChangesUpdate, hasChangesCreate, selectedAmbulance]);

  const table = useServerTable<{ input?: string; ambulance_type?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;

  const { commonListData: ambTypeCodes } = useCommonCodes({ optionParams: 'upper-code=AMB_TP' });
  const { commonListData: ambStatusCodes } = useCommonCodes({ optionParams: 'upper-code=AMB_ST' });
  const { commonListData: ambCondCodes } = useCommonCodes({ optionParams: 'upper-code=AMB_COND' });

  const toOptions = useCallback(
    (data: typeof ambTypeCodes, upperCode: string) =>
      data?.[upperCode]?.code?.map((code, index) => ({
        value: code ?? '',
        label: data[upperCode]?.name[index] ?? '',
      })) ?? [],
    []
  );

  const ambulanceTypeOptions = useMemo(
    () => toOptions(ambTypeCodes, 'AMB_TP'),
    [ambTypeCodes, toOptions]
  );
  const ambulanceStatusOptions = useMemo(
    () => toOptions(ambStatusCodes, 'AMB_ST'),
    [ambStatusCodes, toOptions]
  );
  const ambulanceConditionOptions = useMemo(
    () => toOptions(ambCondCodes, 'AMB_COND'),
    [ambCondCodes, toOptions]
  );

  // Full list (not just the id/name dropdown) - the design shows PIC Name / PIC
  // Phone Number / Healthcare Telephone as read-only fields derived from the
  // selected Ownership Name, so a lookup by id needs those columns too.
  const { data: healthCareFacilityListData } = useQuery<HealthCareFacilityListResponse>({
    queryKey: ['healthCareFacilityListData', 'size=0'],
    queryFn: () => fetchHealthCareFacilityList('size=0'),
    staleTime: 1000 * 60 * 5,
  });
  const healthCareFacilityRows = useMemo(
    () => healthCareFacilityListData?.data?.rows ?? [],
    [healthCareFacilityListData]
  );
  const healthCareFacilityOptions = useMemo(
    () =>
      healthCareFacilityRows.map((item) => ({
        value: String(item.health_care_facility_id),
        label: item.name,
      })),
    [healthCareFacilityRows]
  );

  const [formValues, setFormValues] = useState<AmbulanceFormValues>(emptyFormValues);
  const emptyDetail = useCallback(() => setFormValues(emptyFormValues), []);

  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });
  }, []);

  const enableInitialButtons = useCallback(() => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  }, [disableAllButtons]);

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false, delete: false, create: false }));
  }, [disableAllButtons]);

  const enableRegisterButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  }, [disableAllButtons]);

  const reloadCallback = useCallback(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedAmbulance({ ambulance_id: null });
    setNewId(null);
  }, [enableInitialButtons, emptyDetail]);

  const {
    ambulanceListData,
    detailAmbulanceData,
    deleteAmbulance,
    createAmbulance,
    updateAmbulance,
    refetchDetailAmbulance,
  } = useAmbulanceManagement({
    ambulanceId: selectedAmbulance?.ambulance_id,
    queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      refetchDetailAmbulance();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      setNewId(responseData.ambulance_id);
    },
  });

  const pageData = ambulanceListData?.data;
  const rows = pageData?.rows ?? [];
  const dataAmbulance = detailAmbulanceData?.data;

  const ambulanceColumns = useMemo(
    () => [
      {
        title: t('cmn > no'),
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('ambulance > ambulance id'),
        field: 'ambulance_id',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: t('ambulance > police number'),
        field: 'police_number',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('ambulance > ambulance type'),
        field: 'ambulance_type_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('cmn > vehicle type'),
        field: 'vehicle_type',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('ambulance > ownership name'),
        field: 'health_care_facility_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('cmn > status'),
        field: 'ambulance_status_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('facility > pic name'),
        field: 'ambulance_pic_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('facility > pic phone no'),
        field: 'ambulance_pic_phone',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size, t]
  );

  const detailToFormValues = useCallback(
    (data: AmbulanceDetailData): AmbulanceFormValues => ({
      ambulance_id: data.ambulance_id,
      police_number: data.police_number,
      ambulance_type: data.ambulance_type,
      ambulance_status: data.ambulance_status,
      ambulance_condition: data.ambulance_condition,
      vehicle_type: data.vehicle_type,
      manufacturer_year: data.manufacturer_year,
      health_care_facility_id: data.health_care_facility_id,
      pic_name: data.ambulance_pic_name,
      pic_phone_number: data.ambulance_pic_phone,
      health_care_pic_phone: data.health_care_pic_phone,
    }),
    []
  );

  useEffect(() => {
    if (dataAmbulance) setFormValues(detailToFormValues(dataAmbulance));
  }, [dataAmbulance, detailToFormValues]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      setFormValues((prev) => {
        if (name === 'health_care_facility_id') {
          const facility = healthCareFacilityRows.find(
            (item) => String(item.health_care_facility_id) === value
          );
          return {
            ...prev,
            health_care_facility_id: value,
            pic_name: facility?.ambulance_pic_name ?? null,
            pic_phone_number: facility?.ambulance_pic_phone ?? null,
            health_care_pic_phone: facility?.health_care_pic_phone ?? null,
          };
        }
        return { ...prev, [name]: value };
      });

      if (isNewClicked) {
        setHasChangesCreate(true);
      } else if (selectedAmbulanceRef.current?.ambulance_id) {
        setHasChangesUpdate(true);
      }
    },
    [isNewClicked, healthCareFacilityRows]
  );

  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    resizableRows: false,
    index: 'ambulance_id',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    selectableRowsCheck: (row: RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({ input: inputVal || undefined });
    },
    [table]
  );

  const handleReset = useCallback(() => {
    setTypeFilter('');
    table.reset();
  }, [table]);

  const handleTypeFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setTypeFilter(e.target.value);
      table.setFilters({ ambulance_type: e.target.value || undefined });
    },
    [table]
  );

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const selectRow = () => {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedAmbulance({ ambulance_id: rowData.ambulance_id });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setIsNewClicked(false);
        enableUPDATEButtons();

        requestAnimationFrame(() => nameRef.current?.focus());
      };

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(selectRow);
      } else {
        selectRow();
      }
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = useCallback(() => {
    const startNew = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedAmbulance({ ambulance_id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    };

    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(startNew);
    } else {
      startNew();
    }
  }, [hasChangesCreate, hasChangesUpdate, enableRegisterButtons, t, emptyDetail]);

  const handleCancelButtonClick = useCallback(() => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedAmbulance({ ambulance_id: null });
        enableInitialButtons();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
    }
  }, [
    hasChangesUpdate,
    hasChangesCreate,
    isNewClicked,
    enableInitialButtons,
    reloadCallback,
    t,
    emptyDetail,
  ]);

  const buildPayload = useCallback((): CreateAmbulancePayload | null => {
    const {
      police_number,
      ambulance_type,
      ambulance_status,
      ambulance_condition,
      manufacturer_year,
      health_care_facility_id,
    } = formValues;

    if (
      !police_number?.trim() ||
      !ambulance_type ||
      !ambulance_status ||
      !ambulance_condition ||
      !manufacturer_year?.trim() ||
      !health_care_facility_id
    ) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > required values'));
      return null;
    }

    // The Years input's `pattern` prop only filters keystrokes (GeneralInput
    // rejects a value the regex fails), so it can hold 1-3 digits. The DB's
    // ambulance_manufacturer_year_chk requires exactly 4 - check here so a
    // short year is caught before it becomes a 422 constraint violation.
    if (!/^[0-9]{4}$/.test(manufacturer_year.trim())) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > invalid manufacturer year'));
      return null;
    }

    return {
      police_number: police_number.trim(),
      ambulance_type,
      ambulance_status,
      ambulance_condition,
      vehicle_type: formValues.vehicle_type || null,
      manufacturer_year: manufacturer_year.trim(),
      health_care_facility_id: Number(health_care_facility_id),
    };
  }, [formValues, t]);

  const handleRegistButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload) return;
    createAmbulance(payload);
  }, [createAmbulance, buildPayload]);

  const handleConfirmButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload || !formValues.ambulance_id) return;
    const updatePayload: UpdateAmbulancePayload = {
      ...payload,
      ambulance_id: Number(formValues.ambulance_id),
    };
    updateAmbulance({
      ambulanceData: updatePayload,
      params: { ambulance_id: updatePayload.ambulance_id },
    });
  }, [formValues, updateAmbulance, buildPayload]);

  const handleDeleteButtonClick = useCallback(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      if (selectedAmbulance?.ambulance_id) deleteAmbulance(selectedAmbulance.ambulance_id);
    });
  }, [selectedAmbulance, deleteAmbulance, t]);

  const logData = dataAmbulance
    ? [
        { label: t('cmn > registered by'), value: dataAmbulance.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataAmbulance.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataAmbulance.registered_time))
            : dataAmbulance.registered_time,
        },
        { label: t('cmn > updated by'), value: dataAmbulance.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataAmbulance.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataAmbulance.updated_time))
            : dataAmbulance.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>{t('ASSET')}</h3>
        <h3>&gt;</h3>
        <h3>{t('ASSET-AMBULANCE')}</h3>
      </div>

      <ContainerCard>
        <Filtering
          searchRef={searchRef}
          labelSelect={t('cmn > filter by type')}
          placeholder={t('ambulance > search by id or police number')}
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={typeFilter}
            onChange={handleTypeFilterChange}
            options={[{ value: '', label: t('cmn > all') }, ...ambulanceTypeOptions]}
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={rows}
            columns={ambulanceColumns}
            layout="fitDataStretch"
            options={optionsTabulator}
            className="tabulator-custom"
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              dataSorted: remoteSortHandler(table, AMBULANCE_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current || !newId) return;
                try {
                  const row = tbRef.current.getRow(newId) || null;
                  if (row && typeof row.select === 'function') {
                    tbRef.current.scrollToRow(row, 'bottom', true);
                    handleRowSelected(row);
                  }
                } catch {
                  /* row not found */
                }
              },
            }}
          />
        </div>
        <TablePagination
          total={pageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={pageData?.total_pages ?? 0}
          onPageChange={table.setPage}
          centered
        />
      </ContainerCard>

      <ContainerCard className="flex flex-col">
        <div className="grid grid-cols-2 items-start gap-6">
          <div className="grid grid-cols-1 gap-3">
            <DetailForm
              className="items-center!"
              label={t('cmn > id')}
              disabled={true}
              value={formValues.ambulance_id ?? ''}
              name="ambulance_id"
            />
            <DetailForm
              className="items-center!"
              label={t('ambulance > police number')}
              formRef={nameRef}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.police_number || ''}
              name="police_number"
              maxLength={30}
            />
            <DetailForm
              className="items-center!"
              label={t('ambulance > ambulance type')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.ambulance_type || ''}
              name="ambulance_type"
              optionSelect={ambulanceTypeOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('cmn > vehicle type')}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.vehicle_type || ''}
              name="vehicle_type"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('ambulance > years')}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.manufacturer_year || ''}
              name="manufacturer_year"
              maxLength={4}
              pattern={'^[0-9]*$'}
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <DetailForm
              className="items-center!"
              label={t('ambulance > ownership name')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={
                formValues.health_care_facility_id ? String(formValues.health_care_facility_id) : ''
              }
              name="health_care_facility_id"
              optionSelect={healthCareFacilityOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('cmn > status')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.ambulance_status || ''}
              name="ambulance_status"
              optionSelect={ambulanceStatusOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('ambulance > condition')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.ambulance_condition || ''}
              name="ambulance_condition"
              optionSelect={ambulanceConditionOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('facility > pic name')}
              disabled={true}
              value={formValues.pic_name || ''}
              name="pic_name"
            />
            <DetailForm
              className="items-center!"
              label={t('facility > pic phone no')}
              disabled={true}
              value={formValues.pic_phone_number || ''}
              name="pic_phone_number"
            />
            <DetailForm
              className="items-center!"
              label={t('ambulance > healthcare telephone')}
              disabled={true}
              value={formValues.health_care_pic_phone || ''}
              name="health_care_pic_phone"
            />
          </div>
        </div>
        <hr className="border-t border-gray-300" />
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
          <div className="flex-none">
            <ButtonGroup
              onClickDelete={handleDeleteButtonClick}
              onClickNew={handleNewButtonClick}
              onClickCancel={handleCancelButtonClick}
              onClickRegist={handleRegistButtonClick}
              onClickConfirm={handleConfirmButtonClick}
              isNewClicked={isNewClicked}
              cancelButtonState={buttonState.cancel}
              confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
              deleteButtonState={buttonState.delete}
              newButtonState={hasChangesCreate ? false : buttonState.create}
            />
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default AmbulanceManagement;
